/**
 * Token endpoint API. Call with the app's AUTH_TOKEN_URL (from env).
 */

export interface AuthenticateRequest {
	username: string;
	password: string;
	workspaceId?: string;
}

export interface AuthenticateResponse {
	status_code: number;
	access_token: string;
	token_type: string;
	refresh_token: string;
}

interface PythonErrorResponse {
	error: string;
	error_description: string;
}

export class PythonRequestError extends Error {
	constructor(
		public readonly error: string,
		public readonly description: string,
		public readonly status: number
	) {
		super(error);
	}
}

/**
 * Calls the token endpoint (password grant). Pass tokenUrl from app env (e.g. AUTH_TOKEN_URL).
 */
export async function doAuthenticate(
	tokenUrl: string,
	{ username, password, workspaceId }: AuthenticateRequest
): Promise<AuthenticateResponse> {
	const response = await fetch(tokenUrl, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			grant_type: 'password',
			username,
			password,
			...(workspaceId ? { workspace_id: workspaceId } : {}),
		}),
	});

	if (!response.ok) {
		let err: PythonErrorResponse;
		try {
			err = (await response.json()) as PythonErrorResponse;
		} catch {
			throw new PythonRequestError(
				'request_failed',
				(await response.text()) || `HTTP ${response.status}`,
				response.status
			);
		}
		throw new PythonRequestError(err.error, err.error_description, response.status);
	}

	return response.json() as Promise<AuthenticateResponse>;
}

/**
 * Calls the token endpoint (refresh_token grant). Pass tokenUrl from app env (e.g. AUTH_TOKEN_URL).
 * Throws PythonRequestError when the refresh fails (e.g. invalid_grant).
 * Matches legacy behavior: on !response.ok throws with fixed message, no response body parsing.
 */
export async function doRefreshToken(tokenUrl: string, refreshToken: string): Promise<AuthenticateResponse> {
	const response = await fetch(tokenUrl, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ grant_type: 'refresh_token', refresh_token: refreshToken }),
	});

	if (!response.ok) {
		throw new PythonRequestError('invalid_grant', 'Invalid refresh token', response.status);
	}

	return response.json() as Promise<AuthenticateResponse>;
}
